/**
 * PRP-WEB-1 W3.1: the full-screen plan's viewport. Drag to pan, wheel and pinch to zoom
 * (pointer events, no dependency), Fit / + / - buttons, keys + - 0. Zoom and pan only change
 * the view box; the plan is never touched. A drag never selects the room it ended on.
 *
 * PRP-WEB-2 W2.1: with an `editor`, every press is offered to it first (in plan feet). A press it
 * claims (a room, wall, door or handle) is a drag for the editor once it moves past the slop;
 * anything else pans, and the middle button always pans. A press that did not move is the
 * editor's tap; right-click and a 500 ms touch long-press open its menu.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import type { Plan, Pt } from '@sketch-core';
import { Maximize2, Minus, Plus } from 'lucide-react';
import { PlanCanvas } from './PlanCanvas';
import { centerOf, clientToPlan, feetPerPx, panBy, zoomAt, zoomLevel, ZOOM_STEP, type ViewBox } from './viewBox';

/** Pixels a press may wander before it is a pan instead of a click. */
const DRAG_SLOP_PX = 4;
/** Hit reach in screen pixels: a mouse is precise, a finger is not (min touch target / 2). */
const HIT_PX_MOUSE = 8;
const HIT_PX_TOUCH = 22;
/** A touch held this long opens the menu (W2.3). */
export const LONG_PRESS_MS = 500;

/** A pointer event as the editor sees it: modifiers, and the hit reach and scale at this zoom. */
export interface EditorPointer {
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  touch: boolean;
  tolFt: number;
  pxPerFt: number;
}

/** The editor's side of the viewport (PRP-WEB-2); every point is in plan feet. */
export interface ViewportEditor {
  /** True when the editor takes this press (it will be a drag, not a pan). */
  press: (wp: Pt, p: EditorPointer) => boolean;
  dragStart: (wp0: Pt, wp: Pt, p: EditorPointer) => void;
  dragMove: (wp: Pt, p: EditorPointer) => void;
  dragEnd: () => void;
  /** A second finger arrived mid-drag: drop the drag. */
  cancel: () => void;
  tap: (wp: Pt, p: EditorPointer) => void;
  hover: (wp: Pt | null, p: EditorPointer) => void;
  menu: (wp: Pt, client: Pt, p: EditorPointer) => void;
  dblclick: (wp: Pt, p: EditorPointer) => void;
}

export interface PlanViewportProps {
  plan: Plan;
  view: ViewBox;
  fit: ViewBox;
  onViewChange: (v: ViewBox) => void;
  selectedRoomId?: string | null;
  onRoomClick?: (rid: string) => void;
  /** A click on the floor outside every room. */
  onBackgroundClick?: () => void;
  overlay?: ReactNode;
  underlay?: ReactNode;
  editor?: ViewportEditor;
  /** The cursor over the plan (the editor's hover: move, resize); default grab. */
  cursor?: string;
}

const isTyping = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
};

export function PlanViewport({ plan, view, fit, onViewChange, selectedRoomId, onRoomClick, onBackgroundClick, overlay, underlay, editor, cursor }: PlanViewportProps) {
  const box = useRef<HTMLDivElement>(null);
  const viewRef = useRef(view);
  viewRef.current = view;
  const setView = useRef(onViewChange);
  setView.current = onViewChange;
  const pointers = useRef(new Map<number, Pt>());
  const gesture = useRef<{
    start: Pt; moved: boolean; pinch?: { dist: number; mid: Pt };
    /** The editor took this press; `dragging` once it passed the slop. */
    claimed?: boolean; dragging?: boolean; pan?: boolean; touch?: boolean; done?: boolean;
  } | null>(null);
  const swallowClick = useRef(false);
  const longTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ed = useRef(editor);
  ed.current = editor;
  const clearLong = () => { if (longTimer.current) { clearTimeout(longTimer.current); longTimer.current = null; } };
  useEffect(() => clearLong, []);

  const rect = () => box.current?.getBoundingClientRect() ?? { left: 0, top: 0, width: 0, height: 0 };
  const zoomBy = (factor: number, at?: Pt) => setView.current(zoomAt(viewRef.current, factor, at ?? centerOf(viewRef.current), fit));

  // Wheel: a native listener, because React's is passive and the page must not scroll.
  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const at = clientToPlan(viewRef.current, r, e.clientX, e.clientY);
      // Trackpad pinch arrives as ctrl+wheel with small deltas; scale both the same way.
      const factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015));
      setView.current(zoomAt(viewRef.current, factor, at, fit));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [fit]);

  // Keys + - 0, unless the user is typing in the panel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomBy(ZOOM_STEP); }
      else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomBy(1 / ZOOM_STEP); }
      else if (e.key === '0') { e.preventDefault(); setView.current(fit); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fit]);

  const two = (): [Pt, Pt] => {
    const [a, b] = [...pointers.current.values()];
    return [a, b];
  };

  const info = (e: { ctrlKey: boolean; metaKey: boolean; altKey: boolean; shiftKey: boolean }, touch: boolean): EditorPointer => {
    const r = rect();
    const k = feetPerPx(viewRef.current, r.width, r.height);
    return { ctrl: e.ctrlKey || e.metaKey, alt: e.altKey, shift: e.shiftKey, touch, tolFt: (touch ? HIT_PX_TOUCH : HIT_PX_MOUSE) * k, pxPerFt: 1 / k };
  };
  const toPlan = (cx: number, cy: number) => clientToPlan(viewRef.current, rect(), cx, cy);

  const onPointerDown = (e: React.PointerEvent) => {
    const touch = e.pointerType !== 'mouse';
    const middle = e.pointerType === 'mouse' && e.button === 1;
    if (e.pointerType === 'mouse' && e.button !== 0 && !middle) return;
    if (middle) e.preventDefault(); // no autoscroll
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) {
      const claimed = !middle && !!ed.current?.press(toPlan(e.clientX, e.clientY), info(e, touch));
      gesture.current = { start: { x: e.clientX, y: e.clientY }, moved: false, claimed, pan: middle, touch };
      if (claimed) { try { box.current?.setPointerCapture(e.pointerId); } catch { /* jsdom */ } }
      if (touch && ed.current) {
        clearLong();
        const at = { x: e.clientX, y: e.clientY };
        const pi = info(e, true);
        longTimer.current = setTimeout(() => {
          longTimer.current = null;
          const g = gesture.current;
          if (!g || g.moved) return;
          g.done = true;
          ed.current?.menu(toPlan(at.x, at.y), at, pi);
        }, LONG_PRESS_MS);
      }
    }
    if (pointers.current.size === 2 && gesture.current) {
      clearLong();
      const g = gesture.current;
      if (g.dragging) ed.current?.cancel();
      g.claimed = false;
      g.dragging = false;
      const [a, b] = two();
      g.pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
      g.moved = true;
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const prev = pointers.current.get(e.pointerId);
    const g = gesture.current;
    if (!prev || !g) {
      if (e.pointerType === 'mouse' && !g) ed.current?.hover(toPlan(e.clientX, e.clientY), info(e, false));
      return;
    }
    const cur = { x: e.clientX, y: e.clientY };
    pointers.current.set(e.pointerId, cur);
    const r = rect();
    if (pointers.current.size >= 2 && g.pinch) {
      const [a, b] = two();
      const dist = Math.hypot(a.x - b.x, a.y - b.y), mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      let v = viewRef.current;
      const k = feetPerPx(v, r.width, r.height);
      v = panBy(v, -(mid.x - g.pinch.mid.x) * k, -(mid.y - g.pinch.mid.y) * k);
      if (g.pinch.dist > 0 && dist > 0) v = zoomAt(v, dist / g.pinch.dist, clientToPlan(v, r, mid.x, mid.y), fit);
      g.pinch = { dist, mid };
      setView.current(v);
      return;
    }
    if (g.done) return;
    if (!g.moved && Math.hypot(cur.x - g.start.x, cur.y - g.start.y) < DRAG_SLOP_PX) return;
    if (!g.moved) {
      g.moved = true;
      clearLong();
      try { box.current?.setPointerCapture(e.pointerId); } catch { /* jsdom, or the pointer is gone */ }
      if (g.claimed) {
        g.dragging = true;
        ed.current?.dragStart(toPlan(g.start.x, g.start.y), toPlan(cur.x, cur.y), info(e, !!g.touch));
        return;
      }
    }
    if (g.dragging) { ed.current?.dragMove(toPlan(cur.x, cur.y), info(e, !!g.touch)); return; }
    const k = feetPerPx(viewRef.current, r.width, r.height);
    setView.current(panBy(viewRef.current, -(cur.x - prev.x) * k, -(cur.y - prev.y) * k));
  };

  const onPointerEnd = (e: React.PointerEvent) => {
    clearLong();
    const had = pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (g?.moved || g?.done) swallowClick.current = true;
    if (had && g && pointers.current.size === 0) {
      if (g.dragging) ed.current?.dragEnd();
      else if (!g.moved && !g.done && !g.pan && e.type === 'pointerup' && ed.current) {
        swallowClick.current = true;
        ed.current.tap(toPlan(e.clientX, e.clientY), info(e, !!g.touch));
      }
    }
    if (pointers.current.size === 0) gesture.current = null;
    else if (g) g.pinch = undefined;
  };

  const onContextMenu = (e: React.MouseEvent) => {
    if (!ed.current) return;
    e.preventDefault();
    ed.current.menu(toPlan(e.clientX, e.clientY), { x: e.clientX, y: e.clientY }, info(e, false));
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    if ((e.target as Element).closest?.('button')) return;
    ed.current?.dblclick(toPlan(e.clientX, e.clientY), info(e, false));
  };

  const onClickCapture = (e: React.MouseEvent) => {
    if ((e.target as Element).closest?.('button')) return;
    if (swallowClick.current || (ed.current && e.detail > 0)) {
      swallowClick.current = false;
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const onClick = (e: React.MouseEvent) => {
    if (!(e.target as Element).closest?.('[data-room]')) onBackgroundClick?.();
  };

  const level = zoomLevel(view, fit);
  return (
    <div
      ref={box}
      data-testid="plan-viewport"
      className="relative h-full w-full touch-none select-none overflow-hidden bg-[var(--bg-1)]"
      style={{ cursor: gesture.current?.moved && !gesture.current.dragging ? 'grabbing' : cursor ?? 'grab' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onPointerLeave={(e) => { if (!gesture.current) ed.current?.hover(null, info(e, false)); }}
      onClickCapture={onClickCapture}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onDoubleClick={onDoubleClick}
    >
      <PlanCanvas plan={plan} view={view} selectedRoomId={selectedRoomId} onRoomClick={onRoomClick} overlay={overlay} underlay={underlay}
        className="absolute inset-0 block h-full w-full" />
      <div className="absolute right-3 top-3 flex flex-col gap-1.5" onPointerDown={(e) => e.stopPropagation()}>
        <ZoomButton label="Zoom in" onClick={() => zoomBy(ZOOM_STEP)}><Plus /></ZoomButton>
        <ZoomButton label="Zoom out" onClick={() => zoomBy(1 / ZOOM_STEP)}><Minus /></ZoomButton>
        <ZoomButton label="Fit the plan" onClick={() => setView.current(fit)}><Maximize2 /></ZoomButton>
        <span data-testid="plan-zoom-level" className="rounded-[var(--r-sm)] bg-[var(--bg-3)] px-1 text-center text-[10.5px] tabular-nums text-[var(--tx-lo)]">
          {Math.round(level * 100)}%
        </span>
      </div>
    </div>
  );
}

function ZoomButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick}
      className="grid h-9 w-9 place-items-center rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-3)] text-[var(--tx-hi)] shadow-[var(--sh-sm)] hover:bg-[var(--bg-4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-line)] [&_svg]:h-4 [&_svg]:w-4">
      {children}
    </button>
  );
}

export default PlanViewport;
